import React, { useEffect, useState } from 'react'
import T from 'prop-types'
import { Box, Button, Link as MuiLink, Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'

import InputLabel from 'components/atoms/InputLabel'

const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'
const BLIND_PRICE_INCREMENT = 100
const REMOVE_BUTTON_ERROR_COLOR = 'error.main'
const STEP_PRECISION_EPSILON = 0.00001

export default function CarBlindPrice({
  variant = VARIANT_LIST,
  car,
  handleConfirmBlindPrice = () => {},
  handleShowDetails,
  readOnly = false,
  enabled = true,
  blindOffersRemaining,
  showConfirmButton = true,
  ...others
}) {
  const { t } = useTranslation()

  const currentBlindPrice = car.blindPrice ?? car.reprice
  const groupPrice = car.price

  const [localValue, setLocalValue] = useState('')

  useEffect(() => {
    setLocalValue(currentBlindPrice || '')
  }, [currentBlindPrice])

  const handleChange = (e) => {
    setLocalValue(e.target.value)
  }

  const handleConfirm = (event) => {
    if (event?.preventDefault) {
      event.preventDefault()
    }
    if (event?.stopPropagation) {
      event.stopPropagation()
    }
    if (isDisabled) {
      return
    }
    handleConfirmBlindPrice(
      car.plate,
      currentBlindPrice !== Number(localValue) ? localValue || null : null
    )
  }

  const isDetails = variant === VARIANT_DETAILS

  const hasRemainingOffers =
    blindOffersRemaining === null ||
    blindOffersRemaining === undefined ||
    Number(blindOffersRemaining) > 0
  const hasExistingBlindPrice =
    currentBlindPrice !== null &&
    currentBlindPrice !== undefined &&
    currentBlindPrice !== ''
  const isAttemptingNewBlindPrice =
    !hasExistingBlindPrice &&
    localValue !== null &&
    localValue !== undefined &&
    localValue !== ''
  const canEditBlindPrice =
    enabled &&
    readOnly !== true &&
    (hasRemainingOffers || hasExistingBlindPrice)

  const parsedLocalValue =
    localValue === null || localValue === undefined || localValue === ''
      ? null
      : Number(localValue)
  const hasParsedLocalValue =
    parsedLocalValue !== null && Number.isFinite(parsedLocalValue)
  const hasTypedValue =
    localValue !== null && localValue !== undefined && localValue !== ''
  const parsedCurrentBlindPrice = Number(currentBlindPrice)
  const hasParsedCurrentBlindPrice = Number.isFinite(parsedCurrentBlindPrice)
  const basePrice = hasExistingBlindPrice
    ? parsedCurrentBlindPrice
    : Number(groupPrice)
  const requiredBlindPrice = Number.isFinite(basePrice)
    ? basePrice + BLIND_PRICE_INCREMENT
    : null
  const isRemoveAction =
    hasExistingBlindPrice &&
    hasParsedLocalValue &&
    hasParsedCurrentBlindPrice &&
    parsedLocalValue === parsedCurrentBlindPrice
  const isValidStep =
    hasParsedLocalValue && Number.isFinite(basePrice)
      ? Math.abs((parsedLocalValue - basePrice) % BLIND_PRICE_INCREMENT) <
        STEP_PRECISION_EPSILON
      : true
  const isIncrementInvalid =
    canEditBlindPrice &&
    hasTypedValue &&
    !isRemoveAction &&
    (!hasParsedLocalValue ||
      (requiredBlindPrice !== null &&
        (parsedLocalValue < requiredBlindPrice || !isValidStep)))

  const formattedRequiredBlindPrice =
    requiredBlindPrice !== null
      ? new Intl.NumberFormat('it-IT').format(requiredBlindPrice)
      : null
  const incrementErrorMessage =
    isIncrementInvalid && formattedRequiredBlindPrice
      ? t('carsToBuy.blind.incrementError', {
          amount: formattedRequiredBlindPrice,
          increment: BLIND_PRICE_INCREMENT,
        })
      : ''
  const shortIncrementErrorMessage =
    isIncrementInvalid && formattedRequiredBlindPrice
      ? t('carsToBuy.blind.minimumInput', {
          amount: formattedRequiredBlindPrice,
        })
      : ''

  const isDisabled =
    !enabled ||
    (!localValue && !currentBlindPrice) ||
    readOnly ||
    (!hasRemainingOffers && isAttemptingNewBlindPrice) ||
    isIncrementInvalid

  return (
    <Stack
      direction={isDetails ? undefined : 'row'}
      justifyContent="space-between"
      alignItems={isDetails ? undefined : 'center'}
      mt={2}
      data-testid="car-blind-price"
      {...others}
    >
      {!isDetails && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <MuiLink
            color="primary"
            component={Box}
            onClick={handleShowDetails}
            variant="body2"
            sx={{ cursor: 'pointer' }}
          >
            <strong>{t('carsToBuy.details')}</strong>
          </MuiLink>
        </Box>
      )}
      <Stack
        direction={isDetails ? undefined : 'row'}
        justifyContent={isDetails ? undefined : 'flex-end'}
        mt={2}
      >
        <div>
          {isDetails && (
            <InputLabel
              label={t('carsToBuy.blind.priceLabel')}
              inputId={`${car.plate}.blindPrice.input`}
              sx={{ color: 'text.primary' }}
            />
          )}
          <TextField
            id={`${car.plate}.blindPrice.input`}
            value={localValue || ''}
            label={
              !isDetails
                ? shortIncrementErrorMessage || t('carsToBuy.blind.priceLabel')
                : undefined
            }
            placeholder={isDetails ? t('carsToBuy.reprice.price') : undefined}
            type="number"
            size="small"
            variant={isDetails ? 'outlined' : undefined}
            fullWidth={isDetails}
            disabled={!canEditBlindPrice}
            inputProps={{ step: BLIND_PRICE_INCREMENT }}
            error={
              isDetails ? !!incrementErrorMessage : !!shortIncrementErrorMessage
            }
            helperText={
              isDetails ? incrementErrorMessage || undefined : undefined
            }
            onChange={handleChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                event.stopPropagation()
                handleConfirm(event)
              }
            }}
          />
        </div>
        {isDetails && <Box pb={2} pr={2} />}
        {showConfirmButton && (
          <Button
            type="button"
            color={isRemoveAction ? 'error' : 'primary'}
            variant={isRemoveAction ? 'outlined' : 'contained'}
            disabled={isDisabled}
            onClick={handleConfirm}
            sx={{
              marginLeft: (theme) => (isDetails ? undefined : theme.spacing(2)),
              ...(isRemoveAction
                ? {
                    backgroundColor: 'common.white',
                    color: REMOVE_BUTTON_ERROR_COLOR,
                    borderColor: REMOVE_BUTTON_ERROR_COLOR,
                    '&:hover': {
                      backgroundColor: 'common.white',
                      borderColor: REMOVE_BUTTON_ERROR_COLOR,
                    },
                  }
                : {}),
            }}
          >
            {t(
              `carsToBuy.blind.${
                currentBlindPrice !== Number(localValue)
                  ? 'confirmPrice'
                  : 'removePrice'
              }`
            )}
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

CarBlindPrice.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  car: T.object.isRequired,
  handleConfirmBlindPrice: T.func,
  handleShowDetails: T.func,
  readOnly: T.bool,
  enabled: T.bool,
  blindOffersRemaining: T.number,
  showConfirmButton: T.bool,
}
